import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { partition } from 'lodash';
import { combineLatest, map, startWith } from 'rxjs';
import { selectJob } from 'src/app/job/job-tool.reducer';

import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { OnlineStatusService } from '../../online-status.service';

import { UnsavedChangesBannerActions } from './unsaved-changes-banner.actions';
import { selectBannerChanges, selectCanEditSharedCrewPay, selectSaveChangesButtonDisabledReason } from './unsaved-changes-banner.selectors';

const selectViewModel = createSelector({
    showUnsavedChangesBanner: createSelector(
      selectJob,
      selectBannerChanges,
      (job, changes) => Boolean(job) && changes.length > 0,
    ),
    unsavedChangesBannerIcon: createSelector(
        selectBannerChanges,
        (changes) => {
            const [ pendingChanges, newChanges ] = partition(changes, (c) => c.saving);
            return newChanges.length === 0 && pendingChanges.length > 0 ? 'pi pi-spin pi-spinner' : 'pi pi-exclamation-triangle';
        }
    ),
    saveChangesButtonDisabled: createSelector(
      selectSaveChangesButtonDisabledReason,
      (disabledReason) => Boolean(disabledReason),
    ),
    saveChangesButtonTooltip: selectSaveChangesButtonDisabledReason,
    discardChangesButtonDisabled: createSelector(
        selectBannerChanges,
        (changes) => changes.filter((c) => c.saving).length > 0,
    ),
    discardChangesButtonTooltip: createSelector(
        selectBannerChanges,
        (changes) => {
            const saving = changes.filter((c) => c.saving).length > 0;
            return saving ? 'Please wait for current changes to save' : 'Discard unsaved job changes';
        },
    ),
    // Requirement: `#6951` - the only changes a shared job can have are Crew Pay boxes, and the
    // shared-job overlay covers the strip this banner floats over.
    aboveSharedJobOverlay: selectCanEditSharedCrewPay,
});

@Component({
    selector: 'app-unsaved-changes-banner',
    standalone: true,
    imports: [
        FreyaCommonModule,
    ],
    templateUrl: './unsaved-changes-banner.component.html',
    styleUrls: ['./unsaved-changes-banner.component.scss'],
})
export class UnsavedChangesBannerComponent {

    viewModel$ = combineLatest(
        this.store.select(selectViewModel),
        this.onlineStatusService.isOnline$.pipe(startWith(false)),
    ).pipe(
        map(([ { saveChangesButtonDisabled, saveChangesButtonTooltip, ...rest }, isOnline ]) => ({
            saveChangesButtonDisabled: saveChangesButtonDisabled || !isOnline,
            saveChangesButtonTooltip: isOnline ? saveChangesButtonTooltip : 'You are offline. All changes made will be saved locally',
            ...rest,
        })),
    );

    constructor(
        private store: Store,
        private onlineStatusService: OnlineStatusService,
    ) {}

    onDiscard(): void {
        this.store.dispatch(UnsavedChangesBannerActions.discardUnsavedChangesButtonClicked());
    }

    onSave(): void {
        this.store.dispatch(UnsavedChangesBannerActions.saveChangesButtonClicked());
    }
}
